
import { Component, Vue, Prop, Watch, Emit, Ref } from 'vue-property-decorator'
import BookManager, { EumLoadStatus, EnumScrollDirection } from '@xt/client/utils/book'
import { BookConfig, Chapter, ImgRangeOptions } from '@xt/client/types/book'
import LazyImg from '@xt/www/components/lazyImg/index.vue'
import RealImg from './realImg/index.vue'
import Tabs from './tabs/index.vue'
import lodash from 'lodash'
import { Bind, Debounce } from 'lodash-decorators'
import { reportEntryLog } from '@xt/client/utils/log'
import serverTimeUtil from '@xt/client/experimental/server-time'

type MenuItem = {
  key: string
  name: string
  path: string
}

type Menu = {
  key: string
  name: string
  children: MenuItem[]
}

const defaultBookId = '1'

@Component({
  components: {
    LazyImg,
    Tabs,
    RealImg
  },
  async asyncData({ query, error, redirect }) {
    let _defaultBookId = defaultBookId

    // 判断file参数不存在默认进第一本书
    if ('id' in query && typeof query.id === 'string') {
      _defaultBookId = query.id
    }

    // 判断该用户是否有查看权限，没有则推到首页
    const res = await BookManager.checkHasAuthView(_defaultBookId)

    if (!res) {
      redirect('/')
    }

    const dataSource = await BookManager.getBook(`book-${_defaultBookId}-pc.json`)

    if (!dataSource) {
      redirect('/404')
    }

    return { dataSource, currentBook: _defaultBookId }
  }
})
export default class BooksPage extends Vue {
  showData: boolean = false
  dataSource: BookConfig
  imgList: {
    url: string
    key: string
  }[] = []
  chapterList: Menu[] = []
  rootSubmenuKeys: string[] = []
  openKeys: string[] = []
  // 避免目录关闭状态下，切换章节失效
  currentKeys: string = ''
  // 避免目录关闭情况下点击自身切换images资源
  currentImagListKeys: string = ''
  // tabs数据
  tabsList: string[] = ['02', '1']

  defaultTab: number = 0

  rob: ResizeObserver | null = null
  hasMaxHeight: boolean = false
  menuScrollMax: number = 0

  get imageSize() {
    const { width, height } = this.dataSource

    const designWidth = 840

    const designHeigth = lodash.toInteger((designWidth * height) / width)

    return { width: designWidth, height: designHeigth, originWidth: width, originHeight: height }
  }

  get menuScrollStyle() {
    return this.hasMaxHeight ? { maxHeight: `${this.menuScrollMax}px` } : {}
  }

  get isFirst() {
    return this.rootSubmenuKeys.findIndex(i => i == this.currentKeys) === 0
  }

  get isLast() {
    return this.rootSubmenuKeys.findIndex(i => i == this.currentKeys) === this.rootSubmenuKeys.length - 1
  }

  // 更换书籍数据
  async updateBook(val: number) {
    const dataSource = await BookManager.getBook(`book-${this.tabsList[val]}-pc.json`)

    if (!dataSource) return

    this.dataSource = dataSource

    this.currentBook = this.tabsList[val]

    this.updateData()

    // 切换书籍的时候也要刷新一下
    this.updateMenuScrollMax()

    this.$router.replace({ query: { id: this.tabsList[val] } })
  }

  // 上一章
  handlePrev() {
    let index = this.rootSubmenuKeys.findIndex(i => i == this.currentKeys)

    if (index == -1 || index == 0) return

    this.openKeys = [this.rootSubmenuKeys[--index]]

    this.currentKeys = this.openKeys[0]

    this.onOpenChange(this.openKeys)
  }

  // 下一章
  handleNext() {
    let index = lodash.values(this.rootSubmenuKeys).findIndex(i => i == this.currentKeys)

    if (index == -1 || index == this.rootSubmenuKeys.length - 1) return

    this.openKeys = [this.rootSubmenuKeys[++index]]

    this.currentKeys = this.openKeys[0]

    this.onOpenChange(this.openKeys)
  }

  handleScrollTo(path: string) {
    // 接收path
    const d = document.querySelector(`#${path}`)

    if (!d) return

    d.scrollIntoView({
      behavior: 'smooth'
    })
  }

  onOpenChange(openKeys) {
    // 保持每次只展开一个菜单项
    const latestOpenKey = openKeys.find(key => this.openKeys.indexOf(key) === -1)

    if (this.rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
      this.openKeys = openKeys
    } else {
      this.openKeys = latestOpenKey ? [latestOpenKey] : []
    }

    if (this.openKeys[0]) {
      this.currentKeys = this.openKeys[0]
    }

    // 点击目录处于展开状态且不等于上一次打开的目录时更新ImageList数据
    if (this.openKeys.length === 0 || this.openKeys[0] === this.currentImagListKeys) return

    this.updateImageList()

    // 点击根节点跳转封面
    document.documentElement.scrollTo({ top: 0 })

    // 切换目录的时候也要刷新一下
    this.updateMenuScrollMax()

    // 切换目录时清除图片缓存
    BookManager.cleanData()
  }

  // 切换章节更新图片列表数据
  updateImageList() {
    // 清空上一章节的数据
    this.imgList = []

    const chapterItem = this.dataSource.chapter.find(el => el.key == this.openKeys[0])

    let list: {
      url: string
      key: string
    }[] = []

    if (!chapterItem) return

    if (chapterItem.cover) {
      this.imgList = [
        {
          url: this.dataSource.dir + chapterItem.cover,
          key: chapterItem.key
        }
      ]
    }

    list = chapterItem.subchapter.flatMap(el => {
      return el.path.map((p, idx) => {
        return {
          url: this.dataSource.dir + p,
          key: chapterItem.key + '-' + el.key + '-' + idx
        }
      })
    })

    this.imgList = this.imgList.concat(list)

    this.currentImagListKeys = this.openKeys[0]
  }

  // 转换目录数据为菜单结构
  formatChapter() {
    this.chapterList = this.dataSource.chapter.map(i => {
      let children: MenuItem[] = []

      if (i.subchapter.length > 0) {
        children = i.subchapter.flatMap(j => {
          let subChildren: MenuItem[] = []

          // 处理两个子目录对应一个锚点的情况
          if (j.name.length > 0) {
            subChildren = j.name.map((l, idx) => {
              return {
                key: i.key + '-' + j.key + '-' + idx,
                name: l,
                path: i.key + '-' + j.key + '-0' // 对应的path
              }
            })

            return subChildren
          }

          return [
            {
              key: i.key + '-' + j.key,
              name: j.name[0],
              path: j.path[0]
            }
          ]
        })
      }

      return {
        key: i.key,
        name: i.name,
        children
      }
    })

    // console.log(this.chapterList)
  }

  // 更新数据
  updateData() {
    // 清空上一本书的内容
    this.imgList = []
    this.chapterList = []

    this.openKeys = [this.dataSource.chapter[0].key]
    this.rootSubmenuKeys = this.dataSource.chapter.map(i => i.key)
    this.currentKeys = this.rootSubmenuKeys[0]
    this.formatChapter()
    this.updateImageList()
  }

  @Debounce(300)
  @Bind()
  // 根据屏幕高度判断菜单放不放的下
  updateMenuScrollMax() {
    // 保证每次获取到展开的目录高度
    this.hasMaxHeight = false

    this.$nextTick(() => {
      const el: HTMLElement | null = document.querySelector('#book-menu-target')
      if (el) {
        // 长度超出视口高度时
        if (Math.floor(el.offsetHeight) > window.innerHeight - 66) {
          this.hasMaxHeight = true
        }
        // 操作了目录的高度
        this.menuScrollMax = window.innerHeight - 210
      }
    })
  }

  setWindowOberver() {
    this.$nextTick(() => {
      const el = document.body
      if (el) {
        if (this.rob) {
          this.rob.disconnect()
          this.rob = null
        }

        this.rob = new ResizeObserver(this.updateMenuScrollMax)
        this.rob.observe(el)
      }
    })
  }

  // 初始化
  init() {
    // 获取query参数同步tab状态
    this.defaultTab = this.tabsList.findIndex(tab => tab === this.$route.query.id)

    // 打开对应的目录
    this.openKeys = [this.dataSource.chapter[0].key]
    // 处理所有根目录数据
    this.rootSubmenuKeys = this.dataSource.chapter.map(i => i.key)
    this.currentKeys = this.rootSubmenuKeys[0]

    this.formatChapter()
    this.updateImageList()

    // 兼容大屏完整展示目录，小屏不用折叠滚动目录的场景
    this.updateMenuScrollMax()
    this.setWindowOberver()
  }

  realImgList: Array<{
    url: string | null
    key: string
  }> = []
  BookManager: typeof BookManager = BookManager
  currentBook: string = defaultBookId
  lastActiveKey: number = 0

  get chunkList() {
    return lodash.chunk(this.realImgList, 2)
  }

  @Watch('imgList', { deep: true })
  updateRealImgList(val) {
    // 初始化图片列表数据，避免切换章节时图片列表数据未更新导致会触发预加载限制
    this.realImgList = []
    // 初始化图片 - (加载 - 头部四组)
    this.onLoadRealImgList({
      count: 3
    })
  }

  /**
   * @param data.el 失效图片dom元素
   * 捕捉加载失败(图片链接失效)的图片进行刷新，对于连续多组图片失效的情况，则刷新当前组往下的所有图片
   */
  @Debounce(450, { leading: true, trailing: false })
  @Bind()
  async onRefreshUrlByKey({ el }) {
    const { group, src: imgUrl } = el.dataset as { group: string; src: string } // 组索引
    const activeKey = +group

    console.log('onRefreshUrlByKey', activeKey, imgUrl)

    this.onLoadRealImgList({
      activeKey,
      status: EumLoadStatus.Expired,
      count: 2
    })

    reportEntryLog('debug.book-img.error', {
      now: serverTimeUtil.getServerTime('timestamp'),
      key: new URL(imgUrl).searchParams.get('auth_key') ?? ''
    })
  }

  // 预加载图片 - (加载 - 连续两组)
  @Debounce(450)
  @Bind()
  onActivePreLoad(activeKey: number) {
    console.log('onActivePreLoad', activeKey)
    this.onLoadRealImgList({
      count: 2,
      activeKey
    })
  }

  getScrollDirection(currentActiveKey: number) {
    let direction = EnumScrollDirection.Down

    direction = currentActiveKey > this.lastActiveKey ? EnumScrollDirection.Down : EnumScrollDirection.Up

    // 更新上一次索引
    this.lastActiveKey = currentActiveKey

    return direction
  }

  async onLoadRealImgList(options: Omit<ImgRangeOptions, 'startKey'> & { activeKey?: number } = {}) {
    const { activeKey = 0, status = EumLoadStatus.Success } = options

    const dir = this.getScrollDirection(activeKey)
    const totalGroup = this.chunkList.length
    const nextGroupIdx = Math.min(totalGroup - 1, activeKey + 1)
    const prevGroupIdx = Math.max(0, activeKey - 1)

    /**
     * ** 预加载触发限制条件 **
     * 往下连续组没有缓存的情况下才会发请求 - (从上往下连续滚动)
     * 往上连续组没有缓存的情况下才会发请求 - (从下往上连续滚动)
     */
    if (
      status === EumLoadStatus.Success &&
      dir === EnumScrollDirection.Down &&
      this.chunkList.length &&
      this.chunkList[nextGroupIdx].length &&
      this.chunkList[nextGroupIdx][0].url
    )
      return
    if (
      status === EumLoadStatus.Success &&
      dir === EnumScrollDirection.Up &&
      this.chunkList.length &&
      this.chunkList[prevGroupIdx].length &&
      this.chunkList[prevGroupIdx][0].url
    )
      return

    const formatImgList = this.imgList.map(i => i.url)

    const res = await BookManager.getRealUrlByRange(this.currentBook, formatImgList, {
      ...options,
      startKey: activeKey
    })

    this.realImgList = this.imgList.map((i, idx) => {
      return {
        url: res[idx],
        key: i.key
      }
    })
  }

  created(): void {
    this.init()
  }

  mounted(): void {
    this.$Lazyload.$on('error', this.onRefreshUrlByKey)
  }

  updated(): void {}

  destroyed(): void {
    if (this.rob) {
      this.rob.disconnect()
      this.rob = null
    }

    this.$Lazyload.$off('error', this.onRefreshUrlByKey)

    BookManager.cleanData()
  }
}
